'use client';

import { CopyAll } from '@mui/icons-material';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { IconButton, type SxProps, styled, Typography } from '@mui/material';
import MuiAccordion, { type AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, { type AccordionSummaryProps } from '@mui/material/AccordionSummary';
import type { ReactNode } from 'react';

import { setClipboard } from '@campaign-manager/core/lib/clipboard';
import { campaignManagerTheme } from '@campaign-manager/theme';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion
    disableGutters
    elevation={0}
    square
    {...props}
  />
))(() => ({
  padding: 0,
  border: 0,
  '&::before': {
    display: 'none',
  },
  backgroundColor: 'transparent',
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(() => ({
  padding: '0 0 0 0px',
  '& .MuiAccordionSummary-content': {
    padding: '0 0 0 8px',
  },

  '& .MuiAccordionSummary-expandIconWrapper svg': {
    width: '0.75rem',
    height: '0.75rem',
  },

  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  borderTop: 'none',
}));

interface Props {
  details: string | ReactNode | undefined;
  detailsClipboardText?: string;
  fontSize?: string;
  fontWeight?: number;
  color?: string;
  detailsBackgroundColor?: string;
  detailsColor?: string;
  darkMode?: boolean;
  sx?: SxProps;
  expanded?: boolean;
}

export default function ErrorDetails({
  details,
  detailsClipboardText,
  fontSize = campaignManagerTheme.fontSize.sm,
  fontWeight = 500,
  color = campaignManagerTheme.color.darkRed,
  detailsBackgroundColor = 'transparent',
  detailsColor = campaignManagerTheme.color.grey,
  darkMode = false,
  expanded = true,
  sx,
}: Props) {
  return (
    <Accordion
      sx={{ ...sx }}
      defaultExpanded={expanded}
    >
      <AccordionSummary>
        <Typography
          sx={{
            // Through sx, not the color prop: v9 resolves Typography's color
            // only against palette keys, so a raw value like our theme reds is
            // silently dropped. Both branches here are raw — the default for
            // `color` is campaignManagerTheme.color.darkRed and callers pass
            // theme values too.
            color: darkMode ? campaignManagerTheme.color.lightRed : color,
            fontSize: fontSize,
            fontWeight: fontWeight,
          }}
        >
          Show technical details
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            backgroundColor: detailsBackgroundColor,
            color: detailsColor,
            padding: '8px 12px',
            borderRadius: '4px',
          }}
        >
          <pre
            style={{
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              fontSize: campaignManagerTheme.fontSize.xs,
              fontFamily: campaignManagerTheme.fontFamily.mono,
              color: darkMode ? campaignManagerTheme.color.lightGrey : detailsColor,
              margin: 0,
              padding: 0,
            }}
          >
            {details}
          </pre>
          <IconButton
            onClick={() =>
              setClipboard(detailsClipboardText ?? (typeof details === 'string' ? details : ''))
            }
            edge="end"
            size="small"
            sx={{ color: detailsColor }}
          >
            <CopyAll
              fontSize="small"
              sx={{ color: detailsColor }}
            />
          </IconButton>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
